import { Component, OnInit, Inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthSdkError, OktaAuth } from '@okta/okta-auth-js';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

/**
 * This component is a replacement for the OktaSigninCallback component. The reason is when that component detected
 * any kind of error during signin/callback, it would simply put up a black error page, and sometimes display a cryptic
 * error message. Not a good UX.
 *
 * This component will catch the errors thrown from calling `handleLoginRedirect` and reroute the application to a
 * provided fallback path.  This component also guards against a user trying to navigate directly to this component,
 * which also resulted in the black okta error page, and will reroute the application to the fallback path.
 *
 * The reason(s) for the errors thrown from calling `handleLoginRedirect` are usually a result of two or more tabs
 * trying to login to Okta at the same time. Since the tokens are shared across tabs, there are some contention issues
 * that are exposed. Okta recommends over-riding that component and providing behavior suitable for your application.
 * The changes in this file attempt to mediate and recover the situation by re-routing to app away from
 * this route and to a route appropriate for the apps current auth state.
 */

@Component({
	template: ''
})
export class CustomOktaSigninCallbackComponent implements OnInit {
	public error?: string;
	private fallbackPath = '/';
	private queryParams: Params;

	constructor(
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		private activatedRoute: ActivatedRoute,
		private oktaInterfaceService: OktaInterfaceService,
		private readonly router: Router,
		private readonly logger: ILogger
	) {
		this.logger = logger.createScopedLogger('CustomOktaSigninCallback');
		const { fallbackPath } = activatedRoute.snapshot.data as { fallbackPath?: string };
		this.fallbackPath = fallbackPath ?? '/';
		this.queryParams = this.activatedRoute.snapshot.queryParams;
		this.logger.debug('constructor', 'activatedRoute.snapshot %o', { snapshot: activatedRoute.snapshot });
	}

	private isAuthenticated = toSignal(this.oktaInterfaceService.$isUserAuthenticated);

	public async ngOnInit(): Promise<void> {
		// Okta returns an error response when the
		// authorization code flow is rejected by an inline hook. The URL contains `error` and
		// `state` params but no `code`. If we fall through to the fallbackPath the welcome guard
		// would detect an active Okta session and redirect to a protected route, which in turn
		// triggers a silent re-authentication and loops back here indefinitely.
		// Route to a pending/forbidden status-specific error path instead to break the loop.
		if ('error' in this.queryParams && 'state' in this.queryParams) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const params = this.queryParams as { error: string; error_description?: string };
			const errorDescription: string = params.error_description ?? '';
			const errorPath = this.resolveOktaErrorPath(errorDescription);
			this.logger.error('ngOnInit', 'Okta returned error in callback: %o', {
				error: params.error,
				errorDescription,
				errorPath
			});
			this.oktaAuth.tokenManager.clear();
			this.oktaAuth.transactionManager.clear();
			this.router.navigate([errorPath], { state: { errorDescription } });
			return;
		}

		// detect a user trying to navigate directly to this component by checking
		// for the presence of the 'code' and 'state' query parameters. If they
		// are not present, navigate to the fallbackPath
		if (!('code' in this.queryParams && 'state' in this.queryParams)) {
			this.logger.error('ngOnInit', 'missing required queryParams. routing to: ', this.fallbackPath);
			this.router.navigate([this.fallbackPath]);
			return;
		}

		try {
			await this.oktaAuth.handleLoginRedirect();
		} catch (error) {
			this.logger.error('ngOnInit.handleLoginRedirect() exception', 'Error: %o', {
				error: (error as Error).toString(),
				userAuthenticated: this.isAuthenticated(),
				fallbackPath: this.fallbackPath
			});

			if (error instanceof AuthSdkError) {
				this.oktaAuth.transactionManager.clear();
			}

			if (!this.isAuthenticated()) {
				this.oktaAuth.tokenManager.clear();
			}

			// In all cases, navigate to the designated fallbackPath.
			// That route should properly handle re-routing to Okta if the user isn't
			// logged in, or displaying the apps default route.
			this.router.navigate([this.fallbackPath]);
		}
	}

	/**
	 * Maps the `error_description` from an Okta error callback to a route path using
	 * the configured route data. Known DPS status values encoded by the inline hook:
	 *   - `SHURECLOUD_ACCOUNT_SETUP`  → `pendingErrorPath`
	 *   - `SHURECLOUD_FORBIDDEN`      → `forbiddenErrorPath`
	 * Any unrecognised error description falls back to `errorPath`, then `fallbackPath`.
	 */
	private resolveOktaErrorPath(errorDescription: string): string {
		const { pendingErrorPath, forbiddenErrorPath, errorPath } = this.activatedRoute.snapshot.data as {
			pendingErrorPath?: string;
			forbiddenErrorPath?: string;
			errorPath?: string;
		};
		const normalized = errorDescription.toUpperCase();
		if (normalized === 'SHURECLOUD_ACCOUNT_SETUP') {
			return pendingErrorPath ?? errorPath ?? this.fallbackPath;
		}
		if (normalized === 'SHURECLOUD_FORBIDDEN') {
			return forbiddenErrorPath ?? errorPath ?? this.fallbackPath;
		}
		return errorPath ?? this.fallbackPath;
	}
}
